const STORAGE_PREFIX = "teletrace:ehrCsvColumnMapping:";

export const EHR_MAPPING_FIELDS = [
	{ key: "ehr_visit_id", label: "EHR Visit ID", required: true },
	{ key: "ehr_client_id", label: "EHR Client ID", required: true },
	{ key: "ehr_provider_id", label: "EHR Provider ID", required: true },
	{ key: "date_of_service", label: "Date of Service", required: true },
	{ key: "ehr_time_in", label: "Start Time", required: true },
	{ key: "ehr_time_out", label: "End Time", required: true },
	{ key: "client_name", label: "Client Name", required: false },
	{ key: "provider_name", label: "Provider Name", required: false },
	{ key: "ehr_duration_minutes", label: "Duration", required: false },
];

const AUTO_MAP_PATTERNS = {
	client_name: ["client_name", "patient_name", "member_name"],
	date_of_service: ["date_of_service", "service_date", "dos", "date"],
	ehr_client_id: ["client_id", "ehr_client_id", "patient_id", "client"],
	ehr_duration_minutes: ["duration", "duration_minutes", "ehr_duration", "visit_duration"],
	ehr_provider_id: ["provider_id", "ehr_provider_id", "staff_id", "provider"],
	ehr_time_in: ["start_time", "time_in", "ehr_time_in", "start"],
	ehr_time_out: ["end_time", "time_out", "ehr_time_out", "end"],
	ehr_visit_id: ["visit_id", "ehr_visit_id", "ehr_id"],
	provider_name: ["provider_name", "staff_name", "clinician_name"],
};

const parseCsvLine = (line) => {
	const values = [];
	let current = "";
	let inQuotes = false;

	for (let index = 0; index < line.length; index += 1) {
		const char = line[index];
		if (char === '"') {
			if (inQuotes && line[index + 1] === '"') {
				current += '"';
				index += 1;
			} else {
				inQuotes = !inQuotes;
			}
			continue;
		}
		if (char === "," && !inQuotes) {
			values.push(current.trim());
			current = "";
			continue;
		}
		current += char;
	}

	values.push(current.trim());
	return values;
};

export const normalizeHeader = (value) =>
	String(value || "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");

export const parseCsvPreview = (fileContent) => {
	const lines = String(fileContent || "")
		.replace(/^\uFEFF/, "")
		.split(/\r?\n/)
		.map((line) => line.trim())
		.filter(Boolean);

	if (lines.length < 2) {
		throw new Error("CSV must include a header row and at least one data row.");
	}

	const rawHeaders = parseCsvLine(lines[0]);
	const headers = rawHeaders.map((raw, index) => ({
		index,
		label: raw || `Column ${index + 1}`,
		normalized: normalizeHeader(raw),
		raw: raw || `Column ${index + 1}`,
	}));

	const sampleValues = parseCsvLine(lines[1]);

	return {
		headers,
		rowCount: lines.length - 1,
		sampleValues,
	};
};

export const suggestColumnMapping = (headers) => {
	const mapping = {};
	const usedIndexes = new Set();

	EHR_MAPPING_FIELDS.forEach(({ key }) => {
		const patterns = AUTO_MAP_PATTERNS[key] || [key];
		const match = headers.find((header) => {
			if (usedIndexes.has(header.index)) return false;
			return patterns.includes(header.normalized);
		});

		if (match) {
			mapping[key] = match.raw;
			usedIndexes.add(match.index);
		} else {
			mapping[key] = "";
		}
	});

	return mapping;
};

export const getSampleValue = (headers, sampleValues, selectedColumn) => {
	if (!selectedColumn) return "—";
	const header = headers.find((item) => item.raw === selectedColumn);
	if (!header) return "—";
	const value = sampleValues[header.index];
	return value != null && String(value).trim() !== "" ? String(value) : "—";
};

export const validateColumnMapping = (mapping, headers) => {
	const errors = {};
	const usedColumns = new Map();

	EHR_MAPPING_FIELDS.forEach(({ key, label, required }) => {
		const selected = String(mapping[key] || "").trim();

		if (required && !selected) {
			errors[key] = `${label} is required.`;
			return;
		}

		if (!selected) return;

		const headerExists = headers.some((header) => header.raw === selected);
		if (!headerExists) {
			errors[key] = "Selected column is not available in this file.";
			return;
		}

		if (usedColumns.has(selected)) {
			errors[key] = `Column "${selected}" is already mapped to another field.`;
			errors[usedColumns.get(selected)] = `Column "${selected}" is already mapped to another field.`;
		} else {
			usedColumns.set(selected, key);
		}
	});

	return {
		errors,
		isValid: Object.keys(errors).length === 0,
	};
};

export const isMappingComplete = (mapping) =>
	EHR_MAPPING_FIELDS.filter((field) => field.required).every((field) => String(mapping[field.key] || "").trim());

export const loadSavedColumnMapping = (organizationId) => {
	if (!organizationId) return null;
	try {
		const raw = localStorage.getItem(`${STORAGE_PREFIX}${organizationId}`);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === "object" ? parsed : null;
	} catch {
		return null;
	}
};

export const saveColumnMapping = (organizationId, mapping) => {
	if (!organizationId) return;
	localStorage.setItem(`${STORAGE_PREFIX}${organizationId}`, JSON.stringify(mapping));
};

export const mergeSavedMapping = (savedMapping, headers) => {
	const headerSet = new Set(headers.map((header) => header.raw));
	const merged = {};

	EHR_MAPPING_FIELDS.forEach(({ key }) => {
		const saved = savedMapping?.[key];
		merged[key] = saved && headerSet.has(saved) ? saved : "";
	});

	return merged;
};
